import React from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router';

// Types
import { WorkspaceMeeting } from 'Shared/types/WorkspaceMeeting.type';

// Helpers
import { formatDate } from 'Shared/utils/DateTime.utils';

// Utils
import { createRichTextEditorState } from 'Wpds/components/rich-text/utils/EditorState.utils';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Components
import { WpButton } from 'Wpds/components/button';
import { WpAvatar, WpAvatarEmpty } from 'Wpds/components/avatar';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpDataTableV2 } from 'Wpds/components/table-v2';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';
import SnapshotMeetingItemCheckmarkIcon from './SnapshotMeetingItemCheckmarkIcon';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';

//Styles
import './SnapshotActionItems.scss';

type Props = {
  actionItems;
  workspaceMeeting: WorkspaceMeeting;
};

const SnapshotActionItems = ({ actionItems, workspaceMeeting }: Props) => {
  const { workspaceId } = useParams();
  const history = useHistory();

  const hasActionItems = actionItems.length > 0;
  const baseClassName = 'SnapshotActionItems';

  const Body = ({ actionItem }) => {
    const body = createRichTextEditorState(actionItem.body);

    return (
      <div className={`${baseClassName}__body`}>
        <SnapshotMeetingItemCheckmarkIcon meetingItem={actionItem} workspaceMeeting={workspaceMeeting} />
        <DraftEditor editorState={body} readOnly />
      </div>
    );
  };

  const DueDate = ({ actionItem }) => {
    const date = actionItem.dueBy;

    if (!date) return null;
    return <>{formatDate(date, 'MMM D, YYYY')}</>;
  };

  const Actions = ({ actionItem }) => {
    const { uuid: itemUuid } = actionItem;
    const handleClick = () => {
      history.push(PageUrlsUtils.makeWorkspaceHistoryAgendaItemUrl({ workspaceId, itemUuid }));
    };
    return (
      <WpButton variant="tertiary" size="sm" onClick={handleClick}>
        Details
      </WpButton>
    );
  };

  const Owner = ({ actionItem }) => {
    const assignee = useSelector(state =>
      UsersSelectors.getUserByUserId(state, { userId: actionItem.meetingItemAssignment.userId })
    );
    if (!assignee) return <WpAvatarEmpty size="sm" />;

    const assigneeJS = assignee.toJS();
    const assigneeAvatar = assigneeJS.avatar;
    const assigneeName = assigneeJS.displayName;
    return (
      <div className={`${baseClassName}__owner`}>
        <WpAvatar avatar={assigneeAvatar} size="sm" />
        <span className={`${baseClassName}__owner__name`}>{assigneeName}</span>
      </div>
    );
  };

  const columns = [
    {
      label: 'Action Items',
      selector: row => row.body,
      component: ({ row }) => <Body actionItem={row} />,
      maxWidth: '75%',
      isSortable: false,
    },
    {
      label: 'Due Date',
      selector: row => row.dueBy,
      component: ({ row }) => <DueDate actionItem={row} />,
      isSortable: false,
    },
    {
      label: 'Owner',
      component: ({ row }) => <Owner actionItem={row} />,
      isSortable: false,
    },
    {
      label: 'Actions',
      hideLabel: true,
      rightAlign: true,
      component: ({ row }) => <Actions actionItem={row} />,
    },
  ];

  return (
    <WpContainer className={baseClassName}>
      <WpContainerBody>
        <h5>Action Items</h5>
        {!hasActionItems && <EmptyStateContainer subTitle="There were no action items" grayBackground />}
      </WpContainerBody>
      {hasActionItems && (
        <WpDataTableV2 tableId="snapshotActionItemTable" data={actionItems} columns={columns} showInContainer={false} />
      )}
    </WpContainer>
  );
};

export default SnapshotActionItems;
